// Számla-számítás — KANONIKUS, EGYETLEN megosztott implementáció (docs/modules/admin/invoices.md,
// „Üzleti szabályok / Számítás"). A pénz Decimal-STRING a dráton; a számítás FIXPONTOS
// (string → ×100 skálázott BigInt, HALF_UP kerekítés) — SOHA nem lebegőpontos. A UI élő
// előnézete és a BFF (szerver-oldali újraszámolás) UGYANEZT a függvényt hívja, így az
// optimistic előnézet garantáltan a szerver eredménye.
//
// Kerekítési szabályok (legacy-parity):
//  - HUF:     unit_price egészre kerekít → net = round(qty*unit, 0) → vat = round(net*rate/100, 0)
//             → gross = net + vat (nincs újrakerekítés).
//  - nem-HUF: 2 tizedes mindenhol: net = round(qty*unit, 2), vat = round(net*rate/100, 2),
//             gross = net + vat (a két 2-tizedes összege — újrakerekítés nem szükséges).
//  - Fej-összegek = a tétel-oszlopok SUM-jai, nincs fej-szintű kerekítés.
/** ISO-4217 devizakódok a számlán (HUF egész-kerekítés; EUR/USD 2 tizedes). */
export const INVOICE_CURRENCIES = ['HUF', 'EUR', 'USD'];
/** NAV-ÁFA-kulcsok (a billing Schema Registry enumja 1:1). */
export const INVOICE_VAT_RATES = [
    'VAT_27',
    'VAT_18',
    'VAT_5',
    'VAT_0',
    'AAM',
    'TAM',
    'FAA',
    'EUFAD37',
];
/** ÁFA-kulcs → százalék (VAT_0/AAM/TAM/FAA/EUFAD37 → 0). A mapping forrás-igazsága ITT él. */
export const VAT_RATE_PERCENT = {
    VAT_27: 27,
    VAT_18: 18,
    VAT_5: 5,
    VAT_0: 0,
    AAM: 0,
    TAM: 0,
    FAA: 0,
    EUFAD37: 0,
};
/** Mennyiség-egység kulcsok (kulcsosított — a legacy szabad szövege nem portolódik). */
export const INVOICE_QUANTITY_UNITS = ['piece', 'hour', 'day', 'other'];
/** Bizonylat-típus / kézbesítés / fizetési mód / kibocsátó-kulcs értékkészletek (contract + UI közös). */
export const INVOICE_TYPES = ['invoice', 'proforma'];
/**
 * KÉZBESÍTÉS — mit kérünk a Számlázz.hu-tól: küldje-e ki e-mailben a bizonylatot (`sendEmail`).
 *
 * ⚠️ A `both` SZÁNDÉKOSAN NINCS BENNE (user-döntés). Két okból: élesben SOHA nem használta senki (mérve: 0
 * hozzárendelés és 0 számla), és a rendszerben nem is volt KÜLÖNBSÉGE — a `both` ugyanúgy `sendEmail=true`-t
 * küldött, mint az `electronic`, tehát egy olyan választás volt, ami a kollégának döntésnek látszott, de
 * semmit nem változtatott. A papír-oldal amúgy sem automatizált (azt valaki kinyomtatja és postázza).
 */
export const INVOICE_DELIVERY_TYPES = ['electronic', 'paper'];
export const INVOICE_PAYMENT_METHODS = ['bank_transfer', 'cash', 'card', 'other'];
/**
 * A BIZONYLAT FAJTÁJA a felületen (user-döntés): a típus és a kézbesítés EGY jelvényben — „e-számla" (elektronikus
 * számla), „számla" (papír alapú számla), „díjbekérő" (mindig papír alapú) — és a sztornózott számla „sztornó".
 * A számlák listájának típus-szűrője és a jelvény is ebből számol, ezért a kettő nem térhet el egymástól.
 * (Módosító / helyesbítő számla a rendszerben nincs.)
 */
export const INVOICE_DOC_KINDS = ['e_invoice', 'paper_invoice', 'proforma', 'storno'];
export function invoiceDocKind(doc) {
    if (doc.status === 'storno')
        return 'storno';
    if (doc.type === 'proforma')
        return 'proforma';
    // a hiányzó kézbesítés elektronikus — a generátor és a szerkesztő alapértéke is az
    return doc.deliveryType === 'paper' ? 'paper_invoice' : 'e_invoice';
}
export const INVOICE_ISSUER_KEYS = ['none', 'MOR', 'MDIG'];
/**
 * SEMMIS bizonylat-státuszok: a sztornózott SZÁMLA és az érvénytelenített DÍJBEKÉRŐ.
 *
 * Ezek a sorok nem bevételek — se a lista „Érvénytelenített" csoportján innen, se a bevétel-kimutatás
 * tény-oldalán (`docs/modules/admin/finance.md` §4 „A TÉNY-oldal”). A készlet ITT él, mert MÁR KÉT helyen kell, és a két
 * helynek nem szabad elcsúsznia egymástól: ha egy harmadik semmis státusz jönne, egy soron itt kap helyet.
 */
export const VOID_INVOICE_STATUSES = ['storno', 'cancelled'];
/** Semmis-e a bizonylat (sztornó vagy érvénytelenített díjbekérő). */
export function isVoidInvoiceStatus(status) {
    return VOID_INVOICE_STATUSES.includes(status);
}
/** Nem-negatív Decimal-string, max 2 tizedes (a drót-formátum; a contract Zod-regexe is ez). */
const DECIMAL2_RE = /^\d+(\.\d{1,2})?$/;
/**
 * Decimal-string (max 2 tizedes, nem-negatív) → ×100 skálázott egész (BigInt). Érvénytelen alak →
 * hiba (a contract Zod-ja már kiszűrte; ez a defenzív belső fal).
 */
export function parseDecimal2(value) {
    if (!DECIMAL2_RE.test(value)) {
        throw new Error(`Érvénytelen Decimal-string: "${value}"`);
    }
    const [int = '0', frac = ''] = value.split('.');
    return BigInt(int) * 100n + BigInt((frac + '00').slice(0, 2));
}
/** HALF_UP osztás nem-negatív értékre (minden számla-érték nem-negatív — a bemenet validált). */
function divHalfUp(n, divisor) {
    return (n + divisor / 2n) / divisor;
}
/** ×100 skálázott egész → Decimal-string: HUF → egész (a skálázott érték 100 többszöröse), más → 2 tizedes. */
function formatScaled2(n, currency) {
    if (currency === 'HUF')
        return (n / 100n).toString();
    return `${n / 100n}.${(n % 100n).toString().padStart(2, '0')}`;
}
/** Két Decimal-string érték-egyenlősége (skálázott összehasonlítás — "318.10" ≡ "318.1" ≡ "318.100"…). */
export function decimal2Equals(a, b) {
    return parseDecimal2(normalizeTo2(a)) === parseDecimal2(normalizeTo2(b));
}
/** Hosszabb tizedes-részt 2-re vág (a busz Decimal(12,2) → legfeljebb 2 érdemi tizedes jöhet). */
function normalizeTo2(value) {
    const m = /^(\d+)\.(\d{3,})$/.exec(value);
    if (!m)
        return value;
    const frac = m[2] ?? '';
    // Csak akkor vágunk, ha a levágott rész csupa nulla (érték-azonos); különben érintetlen (→ parse-hiba).
    return /^0*$/.test(frac.slice(2)) ? `${m[1]}.${frac.slice(0, 2)}` : value;
}
/**
 * Egy számla-tétel összegei a kanonikus kerekítési szabályokkal. A `quantity`/`unitPrice`
 * Decimal-string (max 2 tizedes, nem-negatív) — a kliens összeget NEM küldhet, mindig ez számol.
 */
export function calculateInvoiceItem(currency, quantity, unitPrice, vatRate) {
    const qty2 = parseDecimal2(quantity);
    const rate = BigInt(VAT_RATE_PERCENT[vatRate]);
    if (currency === 'HUF') {
        // (1) egységár egészre (HALF_UP) — ELŐBB, a szorzás előtt (legacy-parity)
        const unitInt = divHalfUp(parseDecimal2(unitPrice), 100n);
        // (2) net = round(qty × unit, 0): qty(×100) × unit(egész) → ×100 skála → egészre HALF_UP
        const netInt = divHalfUp(qty2 * unitInt, 100n);
        // (3) vat = round(net × rate / 100, 0); (4) gross = net + vat (nincs újrakerekítés)
        const vatInt = divHalfUp(netInt * rate, 100n);
        return {
            unitPrice: unitInt.toString(),
            netPrice: netInt.toString(),
            vatAmount: vatInt.toString(),
            grossPrice: (netInt + vatInt).toString(),
        };
    }
    // Nem-HUF: 2 tizedes mindenhol
    const unit2 = parseDecimal2(unitPrice);
    // net = round(qty × unit, 2): (×100)·(×100) = ×10000 skála → ×100-ra HALF_UP
    const net2 = divHalfUp(qty2 * unit2, 100n);
    const vat2 = divHalfUp(net2 * rate, 100n);
    return {
        unitPrice: formatScaled2(unit2, currency),
        netPrice: formatScaled2(net2, currency),
        vatAmount: formatScaled2(vat2, currency),
        grossPrice: formatScaled2(net2 + vat2, currency),
    };
}
/**
 * Fej-összegek a tétel-összegekből: netTotal/vatTotal/grossTotal = SUM(netPrice/vatAmount/grossPrice).
 * A bemenet a `calculateInvoiceItem` kimenete (vagy azzal érték-azonos Decimal-string).
 */
export function sumInvoiceTotals(currency, items) {
    let net = 0n;
    let vat = 0n;
    let gross = 0n;
    for (const item of items) {
        net += parseDecimal2(item.netPrice);
        vat += parseDecimal2(item.vatAmount);
        gross += parseDecimal2(item.grossPrice);
    }
    return {
        netTotal: formatScaled2(net, currency),
        vatTotal: formatScaled2(vat, currency),
        grossTotal: formatScaled2(gross, currency),
    };
}
//# sourceMappingURL=invoice.js.map